import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faGraduationCap, faPlay, faPlayCircle, faForward } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { ButtonIcon } from "../Button/Button"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { extractYoutubeId } from "../../Objects/Generics/Helps"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import { useGetLandingVideos } from "../../hooks/Landing/useGetLandingVideos"

type Props = {
  open: boolean
  onClose: () => void
  onStartTutorial: () => void
  onSkipTutorial: () => void
}

export const OwnerOnboardingIntroModal: React.FC<Props> = ({ open, onClose, onStartTutorial, onSkipTutorial }) => {
  const { t } = useTranslation("ownerOnboarding")

  const { isNotMobile } = useMenuMobileContext()

  const { handleGetLandingVideos } = useGetLandingVideos()

  const [desktopVideoUrl, setDesktopVideoUrl] = useState<string | null>(null)

  const [mobileVideoUrl, setMobileVideoUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    handleGetLandingVideos()
      .then((value) => {
        setDesktopVideoUrl(value.ownerOnboardingDesktopYoutubeUrl)
        setMobileVideoUrl(value.ownerOnboardingMobileYoutubeUrl)
      })
      .catch(() => {
        setDesktopVideoUrl(null)
        setMobileVideoUrl(null)
      })
  }, [handleGetLandingVideos, open])

  const videoUrl = isNotMobile ? desktopVideoUrl : mobileVideoUrl

  const videoId = extractYoutubeId(videoUrl ?? "")

  if (!open) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay} style={{ zIndex: 1000 }}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert1} min-w-0`}>
            <TitlePageResponsiveIconSmall
              title={t("intro.title")}
              icon={faGraduationCap}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  typeButton="button"
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"

                  descriptionKey="interactionDescriptions:components.tutorial.ownerOnboardingIntroModal.button.onClose" />
              }
            />

            <Animate type={AnimationTypes.slideUpIn} duration="0.45s">
              <div className="mt-3 overflow-hidden rounded-lg border-2 border-orange-100 bg-black">
                {videoId ? (
                  <iframe
                    title={t("intro.video_title")}
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&playsinline=1`}
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center px-4 text-center text-sm font-bold text-white">
                    {t("intro.video_unavailable")}
                  </div>
                )}
              </div>
            </Animate>

            <Animate type={AnimationTypes.slideUpIn} duration="0.55s">
              <div className="mt-3 rounded-lg border-2 border-orange-100 bg-white p-3">
                <div className="flex items-center gap-2 text-sm font-black uppercase text-orange-700">
                  <FontAwesomeIcon icon={faPlayCircle} />

                  <span>{t("intro.badge")}</span>
                </div>

                <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-700">{t("intro.description")}</p>

                <div className="mt-3 grid gap-2">
                  {["orders", "menu", "setup"].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-2 rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-semibold text-slate-700"
                    >
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-orange-500" />

                      <span>{t(`intro.highlights.${item}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Animate>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <button
                type="button"
                onClick={onStartTutorial}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2.5 text-sm font-black text-black hover:bg-orange-400"
                data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingIntroModal.button.onStartTutorial"
              >
                <FontAwesomeIcon icon={faPlay} />
                {t("intro.start_guided")}
              </button>

              <button
                type="button"
                onClick={onSkipTutorial}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-200 px-4 py-2.5 text-sm font-bold text-slate-800 ring-1 ring-slate-400 hover:bg-slate-200"
                data-interaction-description-key="interactionDescriptions:components.tutorial.ownerOnboardingIntroModal.button.onSkipTutorial"
              >
                <FontAwesomeIcon icon={faForward} />
                {t("intro.skip_guided")}
              </button>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
